/**
 * The document, beside the values that were read out of it.
 *
 * This is the part the first version of this application did not have,
 * and its absence was the whole problem: a reviewer's job is to compare
 * a number against the page it came from, and sending them to another
 * tab to do it makes the screen pointless.
 *
 * **Locating is done in the browser, on the text layer.** The extractor
 * returns values, not coordinates, so when a field is selected its value
 * is matched against the words pdf.js reports for the page and a box is
 * drawn where they sit. That means highlighting works for any provider -
 * including a model that returns no geometry at all - and it costs the
 * backend nothing.
 *
 * Matching is on digits and letters only, so "€7 415.80" on the page
 * matches "7415.80" in the field. Nothing is inferred beyond that: if a
 * value cannot be found, the pane says so rather than highlighting
 * something approximate, because a box drawn in the wrong place is worse
 * than no box at all - it tells a reviewer the number was checked.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import * as pdfjs from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

import { Icon } from "./ui";

// Bundled, not fetched from a CDN: the container makes no external
// request at runtime and works with no network at all.
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export interface Box {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Word {
  page: number;
  text: string;
  box: Box;
}

const SCALE_STEPS = [0.75, 1, 1.25, 1.5, 2];

interface Notice {
  title: string;
  body: string;
}

/**
 * What a reviewer is told when the page itself cannot be shown.
 *
 * The server answers 410 here on purpose, with "the stored file is
 * gone". pdf.js turns that into "Unexpected server response (410) while
 * retrieving PDF <url>", which is the right sentence for whoever wrote
 * the fetch and the wrong one for somebody checking an invoice: it
 * quotes a status code and an address, and leaves them wondering
 * whether the values beside it are trustworthy. They are. The row, the
 * reading and the findings are in the database; only the file is gone.
 */
const GONE: Notice = {
  title: "The original is no longer stored",
  body:
    "Everything read from it is kept - the values beside this pane, the " +
    "findings, and the history of every attempt. Only the page itself is " +
    "gone, so there is nothing here to check them against.",
};

function describe(exc: unknown): Notice {
  const raw = exc instanceof Error ? exc.message : String(exc);
  // 410 is the one status the server chooses deliberately, so it is the
  // one worth translating. Anything else keeps pdf.js's own words: an
  // unrecognised failure is better reported verbatim than guessed at
  // and then described wrongly.
  if (raw.includes("(410)")) return GONE;
  return { title: "This document cannot be displayed", body: raw };
}

function key(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function PdfPane({
  url,
  target,
  available = true,
  onPagesChanged,
}: {
  url: string;
  /** The value to locate, or null. */
  target: string | null;
  /**
   * Whether the server still holds the file. Asked before fetching
   * rather than discovered by failing: a request known to 410 buys
   * nothing but a console error and a slower answer.
   */
  available?: boolean;
  onPagesChanged?: (pages: number) => void;
}) {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [page, setPage] = useState(1);
  const [scaleIndex, setScaleIndex] = useState(1);
  /** Every word on every page, in unscaled page coordinates. */
  const [index, setIndex] = useState<Word[]>([]);
  const [failure, setFailure] = useState<Notice | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const scale = SCALE_STEPS[scaleIndex] ?? 1;

  /** What to render instead of the page, or null when there is a page. */
  const notice: Notice | null = available ? failure : GONE;

  useEffect(() => {
    if (!available) return;
    let cancelled = false;
    const task = pdfjs.getDocument({ url });
    task.promise.then(
      (loaded) => {
        if (cancelled) return;
        setDoc(loaded);
        setPage(1);
        onPagesChanged?.(loaded.numPages);
      },
      (exc: unknown) => {
        if (!cancelled) setFailure(describe(exc));
      },
    );
    return () => {
      cancelled = true;
      void task.destroy();
    };
  }, [url, available, onPagesChanged]);

  // Index every page once, in unscaled coordinates.
  //
  // Built for the whole document rather than the visible page, because a
  // totals block usually sits on the last page while the reviewer is
  // looking at the first. Locating a value has to be able to say "page
  // 2" and take them there; searching only the current page would report
  // "not found" for the numbers that matter most.
  useEffect(() => {
    if (!doc) return;
    let cancelled = false;

    (async () => {
      const collected: Word[] = [];
      for (let number = 1; number <= doc.numPages; number += 1) {
        const rendered = await doc.getPage(number);
        const viewport = rendered.getViewport({ scale: 1 });
        const content = await rendered.getTextContent();
        for (const item of content.items) {
          if (!("str" in item) || !item.str.trim()) continue;
          const tx = pdfjs.Util.transform(viewport.transform, item.transform);
          const height = Math.hypot(tx[2], tx[3]);
          collected.push({
            page: number,
            text: item.str,
            box: {
              page: number,
              x: tx[4],
              y: tx[5] - height,
              width: item.width,
              height,
            },
          });
        }
        if (cancelled) return;
      }
      if (!cancelled) setIndex(collected);
    })().catch(() => {
      /* Indexing is for locating only. A failure here must not stop the
         page from rendering, so it is deliberately swallowed. */
    });

    return () => {
      cancelled = true;
    };
  }, [doc]);

  // Render the current page.
  useEffect(() => {
    if (!doc) return;
    let cancelled = false;

    (async () => {
      const rendered = await doc.getPage(page);
      const viewport = rendered.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;

      // Draw at device resolution and scale back with CSS, so the page
      // is sharp on a retina screen instead of a blurry upscale.
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * ratio);
      canvas.height = Math.floor(viewport.height * ratio);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      setSize({ width: viewport.width, height: viewport.height });

      const context = canvas.getContext("2d");
      if (!context) return;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      await rendered.render({ canvas, canvasContext: context, viewport })
        .promise;
    })().catch((exc: unknown) => {
      if (!cancelled) setFailure(describe(exc));
    });

    return () => {
      cancelled = true;
    };
  }, [doc, page, scale]);

  const found = useMemo((): Box | null => {
    if (!target) return null;
    const needle = key(target);
    if (!needle) return null;

    // An exact cell first: a whole text item that is this value. Then a
    // cell containing it, which covers "€7 415.80" and a line reading
    // "Invoice no. RE671412".
    const exact = index.find((word) => key(word.text) === needle);
    const partial =
      exact ?? index.find((word) => key(word.text).includes(needle));
    return partial?.box ?? null;
  }, [index, target]);

  // The value may be on another page. Go there rather than reporting
  // that it could not be found: on a two-page invoice the totals are
  // almost always on the page the reviewer is not looking at.
  useEffect(() => {
    if (found && found.page !== page) setPage(found.page);
  }, [found, page]);

  const box =
    found && found.page === page
      ? {
          ...found,
          x: found.x * scale,
          y: found.y * scale,
          width: found.width * scale,
          height: found.height * scale,
        }
      : null;
  const missing = target !== null && found === null && index.length > 0;

  useEffect(() => {
    // Scroll a found box into view, but only when it is off screen -
    // yanking the page around when the value is already visible is
    // disorienting.
    if (!box || !canvasRef.current) return;
    const stage = canvasRef.current.closest(".viewer-stage");
    if (!stage) return;
    const top = box.y;
    const visible =
      top > stage.scrollTop && top < stage.scrollTop + stage.clientHeight - 40;
    if (!visible)
      stage.scrollTo({ top: Math.max(0, top - 120), behavior: "smooth" });
  }, [box]);

  return (
    <section className="viewer" aria-label="Document">
      {/* Page numbers, zoom and a link to the file are controls for a
          document. With no document they are furniture that does
          nothing, and a disabled toolbar over an explanation reads as a
          page still loading. */}
      {!notice && (
        <div className="viewer-bar">
          <button
            className="btn ghost sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            aria-label="Previous page"
          >
            {Icon.left({ size: 15 })}
          </button>
          <span className="mono">
            {page} / {doc?.numPages ?? "–"}
          </span>
          <button
            className="btn ghost sm"
            onClick={() => setPage((p) => Math.min(doc?.numPages ?? 1, p + 1))}
            disabled={!doc || page >= doc.numPages}
            aria-label="Next page"
          >
            {Icon.right({ size: 15 })}
          </button>

          <span style={{ marginLeft: "auto" }} />

          {missing && (
            <span className="chip warn">Value not found on this page</span>
          )}
          {box && (
            <span className="chip info">
              {Icon.target({ size: 13 })} Located on page {box.page}
            </span>
          )}

          <button
            className="btn ghost sm"
            onClick={() => setScaleIndex((i) => Math.max(0, i - 1))}
            disabled={scaleIndex === 0}
            aria-label="Zoom out"
          >
            −
          </button>
          <span className="mono">{Math.round(scale * 100)}%</span>
          <button
            className="btn ghost sm"
            onClick={() =>
              setScaleIndex((i) => Math.min(SCALE_STEPS.length - 1, i + 1))
            }
            disabled={scaleIndex === SCALE_STEPS.length - 1}
            aria-label="Zoom in"
          >
            +
          </button>
          <a
            className="btn ghost sm"
            href={url}
            target="_blank"
            rel="noreferrer"
          >
            {Icon.external({ size: 14 })}
          </a>
        </div>
      )}

      <div className="viewer-stage">
        {notice ? (
          <div
            className="empty"
            style={{ border: 0, background: "transparent" }}
          >
            <h2>{notice.title}</h2>
            <p className="note">{notice.body}</p>
          </div>
        ) : (
          <div
            className="page-canvas"
            style={{
              width: size.width || undefined,
              height: size.height || undefined,
            }}
          >
            <canvas ref={canvasRef} />
            {box && (
              <div
                className="locate"
                style={{
                  left: box.x - 3,
                  top: box.y - 3,
                  width: box.width + 6,
                  height: box.height + 6,
                }}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}
